import { makeAutoObservable } from 'mobx';

import axiosAPI from './axiosAPI';
import axiosAuthAPI from './axiosAuthAPI';
import axiosHermesAPI from './axiosHermesAPI';

class ApiStore {
  constructor() {
    makeAutoObservable(this);
  }

  async getQuestionIdsForVideo(videoId) {
    try {
      const response = await axiosAPI.get(`/video/${videoId}/question_ids`);
      return response.data;
    } catch (error) {
      console.error('Error fetching question IDs for video:', error);
      throw error;
    }
  }

  async getQuestionIdsForVideoLevel(videoId, level = null) {
    try {
      const params = level !== null ? { level } : {}; // Send level as a query parameter
      const response = await axiosAuthAPI.get(
        `/api/v1/auto/video/${videoId}/question_ids`,
        {
          params, // Using params for query string parameters
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching question IDs for video:', error);
      throw error;
    }
  }

  async getQuestionDetails(questionId) {
    try {
      const response = await axiosAPI.get(`/question/${questionId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching question details:', error);
      throw error;
    }
  }

  async getNextQuestionIds(
    externalUserId,
    licenseType,
    organizationName,
    level
  ) {
    try {
      const response = await axiosAPI.get(
        `/user/${externalUserId}/license/${licenseType}/next_question_ids`,
        {
          params: {
            organization_name: organizationName,
            level: level,
          },
        }
      );
      return response.data.question_ids;
    } catch (error) {
      console.error('Error fetching next question IDs:', error);
      throw error;
    }
  }

  async getNextQuestion(externalUserId, licenseType, organizationName, level) {
    try {
      const response = await axiosAPI.get(
        `/user/${externalUserId}/license/${licenseType}/next_question`,
        {
          params: {
            organization_name: organizationName,
            level: level,
          },
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching next question:', error);
      throw error;
    }
  }

  async evaluateLevel(externalUserId, licenseType, organizationName) {
    try {
      const response = await axiosAPI.get(
        `/user/${externalUserId}/license/${licenseType}/level`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error retrieving current level:', error);
      throw error;
    }
  }

  async getUserTopicPerformance(externalUserId, organizationName) {
    try {
      const response = await axiosAPI.get(
        `/user/${externalUserId}/topic-performance`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching user topic performance:', error);
      throw error;
    }
  }

  async getOrganization() {
    try {
      const response = await axiosAuthAPI.get(`/api/v1/organizations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching organization details:', error);
      throw error;
    }
  }

  // NEW ENDPOINTS (JWT-based, from /api/v1/infinite_practice)

  /**
   * Retrieve the current level for the authenticated user.
   * @param {string} licenseType - e.g., "auto"
   * @param {string} organizationName
   * @returns {Promise<number>} - The current level of the user.
   */
  async getCurrentLevelAuto(licenseType, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/infinite_practice/user/license/${licenseType}/current_level`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data.current_level;
    } catch (error) {
      console.error('Error retrieving current level (auto):', error);
      throw error;
    }
  }

  /**
   * Fetch the next question IDs using the new JWT-based endpoints.
   * @param {string} licenseType
   * @param {string} organizationName
   * @param {number} level
   */
  async getNextQuestionIdsAuto(licenseType, organizationName, level) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/infinite_practice/license/${licenseType}/next_question_ids`,
        {
          params: {
            organization_name: organizationName,
            level: level,
          },
        }
      );
      return response.data.question_ids;
    } catch (error) {
      console.error('Error fetching next question IDs (auto):', error);
      throw error;
    }
  }

  /**
   * Fetch the next question using the new JWT-based endpoints.
   * @param {string} licenseType
   * @param {string} organizationName
   * @param {number} level
   */
  async getNextQuestionAuto(licenseType, organizationName, level) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/infinite_practice/license/${licenseType}/next_question`,
        {
          params: {
            organization_name: organizationName,
            level: level,
          },
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching next question (auto):', error);
      throw error;
    }
  }

  /**
   * Log an answer to a question using the new JWT-based endpoint.
   * @param {number} questionId
   * @param {boolean} isCorrect
   * @param {string} organizationName
   */
  async answerQuestionAuto(questionId, isCorrect, organizationName) {
    try {
      const response = await axiosAuthAPI.post(
        `/api/v1/infinite_practice/answer_question`,
        {
          questionId,
          isCorrect,
        },
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error answering question (auto):', error);
      throw error;
    }
  }

  /**
   * Evaluate the current level using the new JWT-based endpoint.
   * @param {string} licenseType
   * @param {string} organizationName
   */
  async evaluateLevelAuto(licenseType, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/infinite_practice/license/${licenseType}/level`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error retrieving current level (auto):', error);
      throw error;
    }
  }

  /**
   * Update progress settings for a given level using the new JWT-based endpoint.
   * Requires admin role.
   * @param {string} licenseType
   * @param {number} level
   * @param {string} organizationName
   * @param {number} completionPercentageThreshold
   * @param {number} completionAccuracyThreshold
   * @param {boolean} podcastRequired
   */
  async updateProgressSettingsAuto(
    licenseType,
    level,
    organizationName,
    completionPercentageThreshold,
    completionAccuracyThreshold,
    podcastRequired
  ) {
    try {
      const response = await axiosAuthAPI.put(
        `/api/v1/infinite_practice/license/${licenseType}/level/${level}/progress_settings`,
        {
          completion_percentage_threshold: completionPercentageThreshold,
          completion_accuracy_threshold: completionAccuracyThreshold,
          podcast_required: podcastRequired,
        },
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating progress settings (auto):', error);
      throw error;
    }
  }

  /**
   * Get progress settings for a given level using the new JWT-based endpoint.
   * Requires admin role.
   * @param {string} licenseType
   * @param {number} level
   * @param {string} organizationName
   */
  async getProgressSettingsAuto(licenseType, level, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/infinite_practice/license/${licenseType}/level/${level}/progress_settings`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error retrieving progress settings (auto):', error);
      throw error;
    }
  }

  /**
   * Checks if a podcast is required and not yet generated for a given license type and level.
   * @param {string} licenseType
   * @param {string|number} level
   * @param {string} organizationName
   * @returns {Promise<Object>} - An object with { podcast_required: boolean, podcast_already_generated: boolean }
   */
  async checkPodcastNeed(licenseType, level, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/auto/audio/license/${licenseType}/level/${level}/check_podcast_need`,
        {
          params: { organization_name: organizationName },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking podcast need:', error);
      throw error;
    }
  }

  // Video-related methods

  /**
   * Uploads a video to the backend.
   * @param {FormData} formData - The form data containing the video file and metadata.
   * @returns {Promise<Object>} - The response data from the backend.
   */
  async uploadVideo(formData) {
    try {
      const response = await axiosAuthAPI.post(
        '/api/v1/auto/video/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading video:', error);
      throw error;
    }
  }

  /**
   * Lists videos for a given organization and application.
   * @param {string} organizationName - The name of the organization.
   * @param {string} applicationName - The name of the application.
   * @returns {Promise<Array>} - An array of video objects.
   */
  async listVideos(organizationName, applicationName) {
    try {
      const response = await axiosAuthAPI.get('/api/v1/auto/video/list', {
        params: {
          organization_name: organizationName,
          application_name: applicationName,
        },
      });
      return response.data.videos;
    } catch (error) {
      console.error('Error listing videos:', error);
      throw error;
    }
  }

  /**
   * Deletes a video (soft-delete) identified by its external ID.
   * @param {string} externalId - The external ID of the video to delete.
   * @returns {Promise<Object>} - The response data from the backend.
   */
  async deleteVideo(externalId) {
    try {
      const response = await axiosAuthAPI.delete(
        `/api/v1/auto/video/${externalId}/delete`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  }

  /**
   * Retrieves a signed URL for accessing a video.
   * @param {string} externalId - The external ID of the video.
   * @returns {Promise<Object>} - An object containing the signed URL and other metadata.
   */
  async getSignedUrl(externalId) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/auto/video/${externalId}/signed-url`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting signed URL for video:', error);
      throw error;
    }
  }

  /**
   * Increments the view count for a specific video.
   * @param {string} externalId - The external ID of the video.
   * @param {string} organizationName - The name of the organization.
   * @returns {Promise<Object>} - The response data from the backend.
   */
  async incrementVideoView(externalId, organizationName) {
    try {
      const response = await axiosAuthAPI.post(
        `/api/v1/auto/video/${externalId}/view`,
        {
          organization_name: organizationName,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error incrementing video view:', error);
      throw error;
    }
  }

  /**
   * Retrieves view statistics for a specific video for the current user.
   * @param {string} externalId - The external ID of the video.
   * @param {string} organizationName - The name of the organization.
   * @returns {Promise<Object>} - An object containing view statistics.
   */
  async getVideoViews(externalId, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/auto/video/${externalId}/views`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error getting video views:', error);
      throw error;
    }
  }

  /**
   * Generates a podcast script based on provided parameters.
   * @param {Object} data - The data object containing script generation parameters.
   * @returns {Promise<Object>} - The generated script and related information.
   */
  async generatePodcastScript(data) {
    try {
      const response = await axiosAuthAPI.post(
        '/api/v1/auto/audio/generate-podcast-script',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error generating podcast script:', error);
      throw error;
    }
  }

  /**
   * Retrieves the most failed questions for a user.
   * @param {Object} params - The query parameters for fetching failed questions.
   * @returns {Promise<Object>} - An object containing the most failed questions and related data.
   */
  async getMostFailedQuestions(params) {
    try {
      const response = await axiosAuthAPI.get(
        '/api/v1/auto/audio/most-failed-questions',
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching most failed questions:', error);
      throw error;
    }
  }

  /**
   * Generates a podcast based on provided parameters.
   * @param {Object} data - The data object containing podcast generation parameters.
   * @returns {Promise<Object>} - The generated podcast information.
   */
  async generatePodcast(data) {
    try {
      const response = await axiosAuthAPI.post(
        '/api/v1/auto/audio/generate-podcast',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error generating podcast:', error);
      throw error;
    }
  }

  /**
   * Gets the status of a podcast generation job.
   * @param {string} jobId - The job ID to check.
   * @returns {Promise<Object>} - An object containing the job status information.
   */
  async getPodcastJobStatus(jobId) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/auto/audio/job-status/${jobId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting podcast job status:', error);
      throw error;
    }
  }

  /**
   * Retrieves a signed URL for accessing an audio file.
   * @param {string} audioId - The ID of the audio file.
   * @returns {Promise<Object>} - An object containing the signed URL and related information.
   */
  async getAudioSignedUrl(audioId) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/auto/audio/${audioId}/signed-url`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting signed URL for audio:', error);
      throw error;
    }
  }

  /**
   * Retrieves all podcasts for the current user.
   * @param {string} organizationName - The name of the organization.
   * @param {string} applicationName - The name of the application.
   * @returns {Promise<Array>} - An array of podcast objects.
   */
  async getUserPodcasts(organizationName, applicationName) {
    try {
      const response = await axiosAuthAPI.get(
        '/api/v1/auto/audio/user-podcasts',
        {
          params: {
            organization_name: organizationName,
            application_name: applicationName,
          },
        }
      );
      return response.data.podcasts;
    } catch (error) {
      console.error('Error retrieving user podcasts:', error);
      throw error;
    }
  }

  /**
   * Retrieves the driving sessions for the current student.
   * @param {string} organizationName - The name of the organization.
   * @returns {Promise<Array>} - An array of driving session objects.
   */
  async getStudentDrivingSessions(organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        '/api/v1/driving_sessions/student/driving_sessions',
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data.driving_sessions;
    } catch (error) {
      console.error('Error fetching student driving sessions:', error);
      throw error;
    }
  }

  /**
   * Retrieves GPS data for a specific driving session.
   * @param {number} drivingSessionId - The ID of the driving session.
   * @param {string} organizationName - The name of the organization.
   * @returns {Promise<Array>} - An array of GPS data points.
   */
  async getStudentDrivingSessionGPS(drivingSessionId, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/driving_sessions/student/driving_sessions/${drivingSessionId}/gps`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data.gps_data;
    } catch (error) {
      console.error('Error fetching GPS data for driving session:', error);
      throw error;
    }
  }

  /**
   * Retrieves notes for a specific driving session.
   * @param {number} drivingSessionId - The ID of the driving session.
   * @param {string} organizationName - The name of the organization.
   * @returns {Promise<Array>} - An array of notes.
   */
  async getStudentDrivingSessionNotes(drivingSessionId, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/driving_sessions/student/driving_sessions/${drivingSessionId}/notes`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data.notes;
    } catch (error) {
      console.error('Error fetching notes for driving session:', error);
      throw error;
    }
  }

  /**
   * Retrieves the video URL for a specific driving session.
   * @param {number} drivingSessionId - The ID of the driving session.
   * @param {string} organizationName - The name of the organization.
   * @returns {Promise<string>} - The signed URL of the video.
   */
  async getStudentDrivingSessionVideo(drivingSessionId, organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/driving_sessions/student/driving_sessions/${drivingSessionId}/video`,
        {
          params: {
            organization_name: organizationName,
          },
        }
      );
      return response.data.video_url;
    } catch (error) {
      console.error('Error fetching video URL for driving session:', error);
      throw error;
    }
  }

  // -----------------------------------
  //  STUDENT EXAMS
  // -----------------------------------

  /**
   * Lists all exams for the student, including new fields:
   *  {
   *    examId, title, description, status, score,
   *    startTime, endTime, timeSpentMinutes, category, displayOrder
   *  }
   * GET /api/v1/student_exams/list?organization_name=...&category=...
   */
  async getStudentExams(organizationName, category = null) {
    try {
      const params = { organization_name: organizationName };
      if (category) {
        params['category'] = category;
      }

      const response = await axiosAuthAPI.get('/api/v1/student_exams/list', {
        params,
      });
      // Returns array of objects with new fields: startTime, endTime, timeSpentMinutes, category, displayOrder
      return { data: response.data };
    } catch (error) {
      console.error('Error fetching student exams:', error);
      throw error;
    }
  }

  /**
   * Lists all system exams (org-level).
   * GET /api/v1/student_exams/available?organization_name=...&category=...
   */
  async getAllExams(organizationName, category = null) {
    try {
      const params = { organization_name: organizationName };
      if (category) {
        params['category'] = category;
      }

      const response = await axiosAuthAPI.get(
        '/api/v1/student_exams/available',
        {
          params,
        }
      );
      // array of { ExamID, ExamName, Description, Category, DisplayOrder, IsEnabled, ... }
      return response.data;
    } catch (error) {
      console.error('Error fetching all exams from list_exams:', error);
      throw error;
    }
  }

  /**
   * Gets all unique exam categories for the organization.
   * GET /api/v1/student_exams/categories?organization_name=...
   */
  async getExamCategories(organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        '/api/v1/student_exams/categories',
        {
          params: { organization_name: organizationName },
        }
      );
      // array of strings: ["Basic", "Intermediate", "Advanced"]
      return response.data;
    } catch (error) {
      console.error('Error fetching exam categories:', error);
      throw error;
    }
  }

  /**
   * Starts (or resumes) an exam attempt for the current user.
   * POST /api/v1/student_exams/<exam_id>/start
   */
  async startExamAttempt(examId, organizationName) {
    try {
      const response = await axiosAuthAPI.post(
        `/api/v1/student_exams/${examId}/start`,
        {
          organization_name: organizationName,
        }
      );
      // Typically returns { student_exam_id: 123 }
      return response.data;
    } catch (error) {
      console.error('Error starting exam attempt:', error);
      throw error;
    }
  }

  /**
   * Retrieves the next question in an exam attempt by question index.
   * GET /api/v1/student_exams/<student_exam_id>/next_question?index=N
   */
  async getNextExamQuestion(studentExamId, index = 1) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/student_exams/${studentExamId}/next_question`,
        { params: { index } }
      );
      // Returns question data in shape: { question_id, text, explanation, options, correct_answer, image_data }
      return response.data;
    } catch (error) {
      console.error('Error fetching next question:', error);
      throw error;
    }
  }

  /**
   * Submits an answer for the current question in an exam attempt.
   * POST /api/v1/student_exams/<student_exam_id>/answer
   */
  async submitExamAnswer(studentExamId, questionId, selectedAnswer, isCorrect) {
    try {
      const response = await axiosAuthAPI.post(
        `/api/v1/student_exams/${studentExamId}/answer`,
        {
          question_id: questionId,
          selected_answer: selectedAnswer,
          is_correct: isCorrect,
        }
      );
      return response.data; // { message: "Answer recorded" }
    } catch (error) {
      console.error('Error submitting exam answer:', error);
      throw error;
    }
  }

  /**
   * Finalizes the exam attempt and returns final results.
   * POST /api/v1/student_exams/<student_exam_id>/finalize
   */
  async finalizeExamAttempt(studentExamId, score = 0) {
    try {
      const response = await axiosAuthAPI.post(
        `/api/v1/student_exams/${studentExamId}/finalize`,
        { score }
      );
      return response.data; // { message: "...", ... }
    } catch (error) {
      console.error('Error finalizing exam:', error);
      throw error;
    }
  }

  /**
   * Get instructors for the specified organization (Hermes Chat).
   */
  async getInstructors(organizationName) {
    try {
      const response = await axiosHermesAPI.get('/api/v1/chat/instructors', {
        params: { organization_name: organizationName },
      });
      return response.data; // array of { user_id, user_name }
    } catch (error) {
      console.error('Error fetching instructors:', error);
      throw error;
    }
  }

  /**
   * Get all chat messages for the current user (student).
   */
  async getChatMessages(organizationName, afterMessageId) {
    try {
      // Build query params conditionally
      const params = {
        organization_name: organizationName,
      };
      if (afterMessageId !== null && afterMessageId !== undefined) {
        params.after_message_id = afterMessageId;
      }

      const response = await axiosHermesAPI.get('/api/v1/chat/messages', {
        params,
      });
      // shape: { messages: [ ... ] }
      return response.data.messages || [];
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      throw error;
    }
  }

  /**
   * Post a new message from the student (to AI or instructor).
   * recipient: 'ai' | 'instructor'
   * recipientUserId?: number
   * message: string
   */
  async postChatMessage(
    organizationName,
    recipient,
    message,
    recipientUserId = null
  ) {
    try {
      const payload = {
        recipient,
        message,
      };
      if (recipientUserId) {
        payload.recipient_user_id = recipientUserId;
      }

      const response = await axiosHermesAPI.post(
        '/api/v1/chat/messages',
        payload,
        {
          params: { organization_name: organizationName },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error posting chat message:', error);
      throw error;
    }
  }

  async refreshToken(refreshToken) {
    try {
      const response = await axiosAPI.post('/api/v1/auth/refresh', {
        refresh_token: refreshToken,
      });
      return response.data; // { access_token, refresh_token }
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }

  /**
   * Gets the entire org settings JSON for the given organization.
   * @param {string} organizationName
   * @returns {Promise<{ data: any }>} - The Axios response, with `.data` containing the settings object.
   */
  async getOrgSettings(organizationName) {
    try {
      const response = await axiosAuthAPI.get(
        `/api/v1/orgs/${organizationName}/settings`
      );
      return response; // => { data: { modulos: {...}, configVideos: {...}, configPractica: {...} } }
    } catch (error) {
      console.error('Error fetching org settings:', error);
      throw error;
    }
  }
}

export default new ApiStore();
